import apiClient from './api'

export type EmergencyKind = 'WILDFIRE' | 'FLOOD' | 'EARTHQUAKE' | 'TSUNAMI' | 'VOLCANIC' | 'SECURITY' | 'HAZMAT' | 'OTHER'
export type EmergencyPhase = 'ALERT' | 'RESPONSE' | 'RECOVERY' | 'CLOSED'
export type CommandRole = 'COMMANDER' | 'OPERATIONS' | 'PLANNING' | 'LOGISTICS' | 'SAFETY' | 'LIAISON' | 'INFORMATION' | 'TEAM_LEADER'
export type LogKind =
    | 'EVACUATED' | 'INJURED' | 'DECEASED' | 'MISSING' | 'SHELTERED' | 'ROAD_CLOSURE' | 'FACILITY_AFFECTED'
    | 'COMMUNITY_SUPPORT' | 'RESOURCE' | 'NOTE' | 'COMMAND' | 'PHASE' | 'LEVEL'

export const KIND_LABEL: Record<EmergencyKind, string> = {
    WILDFIRE: 'Incendio forestal', FLOOD: 'Inundación', EARTHQUAKE: 'Sismo', TSUNAMI: 'Tsunami',
    VOLCANIC: 'Erupción volcánica', SECURITY: 'Seguridad', HAZMAT: 'Materiales peligrosos', OTHER: 'Otra',
}
export const PHASE_LABEL: Record<EmergencyPhase, string> = {
    ALERT: 'Alerta', RESPONSE: 'Respuesta', RECOVERY: 'Recuperación', CLOSED: 'Cerrada',
}
export const ROLE_LABEL: Record<CommandRole, string> = {
    COMMANDER: 'Comandante del evento', OPERATIONS: 'Jefe de operaciones', PLANNING: 'Jefe de planificación',
    LOGISTICS: 'Jefe de logística', SAFETY: 'Encargado de seguridad', LIAISON: 'Enlace institucional',
    INFORMATION: 'Encargado de información', TEAM_LEADER: 'Líder de equipo',
}
export const LOG_LABEL: Record<LogKind, string> = {
    EVACUATED: 'Evacuados', INJURED: 'Heridos', DECEASED: 'Fallecidos', MISSING: 'Desaparecidos', SHELTERED: 'Albergados',
    ROAD_CLOSURE: 'Corte de ruta', FACILITY_AFFECTED: 'Instalación afectada', COMMUNITY_SUPPORT: 'Apoyo a la comunidad',
    RESOURCE: 'Recursos', NOTE: 'Nota', COMMAND: 'Mando', PHASE: 'Fase', LEVEL: 'Nivel',
}
/** Tipos que registra el sistema (no se eligen a mano). */
export const SYSTEM_KINDS: LogKind[] = ['COMMAND', 'PHASE', 'LEVEL']

export interface Emergency {
    id: number
    code: string
    title: string
    kind: EmergencyKind
    description: string | null
    level: 1 | 2 | 3
    phase: EmergencyPhase
    triggerNote: string | null
    areaRing: number[][] | null
    startedAt: string
    closedAt: string | null
    reportResults: string | null
    reportGaps: string | null
    reportLessons: string | null
    reportWelfare: string | null
}

export interface EmergencyCommand {
    id: number
    role: CommandRole
    userId: string | null
    personName: string
    organization: string | null
    phone: string | null
    assignedAt: string
    endedAt: string | null
    note: string | null
    active: boolean
}

export interface LogEntry {
    id: number
    kind: LogKind
    quantity: number | null
    description: string
    sector: string | null
    lat: number | null
    lon: number | null
    occurredAt: string
    reportedName: string | null
}

export interface KindTotal { kind: LogKind; quantity: number; entries: number }

export interface EmergencyInput {
    title?: string
    kind?: EmergencyKind
    description?: string
    level?: number
    triggerNote?: string
    area?: number[][]
}

const BASE = '/api/v1/emergencies'

export const emergencyService = {
    async list(includeClosed = false): Promise<Emergency[]> {
        return (await apiClient.get(BASE, { params: { includeClosed } })).data
    },
    async get(id: number): Promise<Emergency> { return (await apiClient.get(`${BASE}/${id}`)).data },
    async create(input: EmergencyInput): Promise<Emergency> { return (await apiClient.post(BASE, input)).data },
    async update(id: number, input: EmergencyInput): Promise<Emergency> { return (await apiClient.put(`${BASE}/${id}`, input)).data },
    async setPhase(id: number, phase: EmergencyPhase, note?: string): Promise<Emergency> {
        return (await apiClient.post(`${BASE}/${id}/phase`, { phase, note })).data
    },
    async setLevel(id: number, level: number, note?: string): Promise<Emergency> {
        return (await apiClient.post(`${BASE}/${id}/level`, { level, note })).data
    },
    async close(id: number, report: { results: string; gaps?: string; lessons?: string; welfare?: string }): Promise<Emergency> {
        return (await apiClient.post(`${BASE}/${id}/close`, report)).data
    },
    async reopen(id: number, note?: string): Promise<Emergency> { return (await apiClient.post(`${BASE}/${id}/reopen`, { note })).data },
    async command(id: number): Promise<EmergencyCommand[]> { return (await apiClient.get(`${BASE}/${id}/command`)).data },
    async assignCommand(id: number, input: { role: CommandRole; personName: string; organization?: string; phone?: string; note?: string }): Promise<EmergencyCommand> {
        return (await apiClient.post(`${BASE}/${id}/command`, input)).data
    },
    async endCommand(id: number, commandId: number): Promise<void> { await apiClient.delete(`${BASE}/${id}/command/${commandId}`) },
    async log(id: number): Promise<LogEntry[]> { return (await apiClient.get(`${BASE}/${id}/log`)).data },
    async addLog(id: number, input: { kind: LogKind; quantity?: number; description: string; sector?: string; lat?: number; lon?: number }): Promise<LogEntry> {
        return (await apiClient.post(`${BASE}/${id}/log`, input)).data
    },
    async deleteLog(id: number, entryId: number): Promise<void> { await apiClient.delete(`${BASE}/${id}/log/${entryId}`) },
    async totals(id: number): Promise<KindTotal[]> { return (await apiClient.get(`${BASE}/${id}/totals`)).data },
}

// ── Reportes de situación (etapa 2) ──
export type SitrepStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED'
export const SITREP_STATUS_LABEL: Record<SitrepStatus, string> = { DRAFT: 'Borrador', SUBMITTED: 'Enviado', APPROVED: 'Aprobado' }

export interface Sitrep {
    id: number
    number: number | null
    status: SitrepStatus
    periodFrom: string
    periodTo: string
    situation: string | null
    actions: string | null
    needs: string | null
    outlook: string | null
    returnNote: string | null
    createdName: string | null
    createdAt: string
    submittedAt: string | null
    approvedName: string | null
    approvedAt: string | null
}

export interface SitrepInput {
    periodFrom?: string
    periodTo?: string
    situation?: string
    actions?: string
    needs?: string
    outlook?: string
}

export const sitrepService = {
    async list(emergencyId: number): Promise<Sitrep[]> { return (await apiClient.get(`${BASE}/${emergencyId}/sitreps`)).data },
    async create(emergencyId: number, input: SitrepInput): Promise<Sitrep> { return (await apiClient.post(`${BASE}/${emergencyId}/sitreps`, input)).data },
    async update(emergencyId: number, id: number, input: SitrepInput): Promise<Sitrep> { return (await apiClient.put(`${BASE}/${emergencyId}/sitreps/${id}`, input)).data },
    async submit(emergencyId: number, id: number): Promise<Sitrep> { return (await apiClient.post(`${BASE}/${emergencyId}/sitreps/${id}/submit`)).data },
    async returnToDraft(emergencyId: number, id: number, note: string): Promise<Sitrep> { return (await apiClient.post(`${BASE}/${emergencyId}/sitreps/${id}/return`, { note })).data },
    async approve(emergencyId: number, id: number): Promise<Sitrep> { return (await apiClient.post(`${BASE}/${emergencyId}/sitreps/${id}/approve`)).data },
    async remove(emergencyId: number, id: number): Promise<void> { await apiClient.delete(`${BASE}/${emergencyId}/sitreps/${id}`) },
    pdfUrl(emergencyId: number, id: number): string { return `${apiClient.defaults.baseURL ?? ''}${BASE}/${emergencyId}/sitreps/${id}/pdf` },
}

// ── Recursos (etapa 3) ──
export type ResourceKind = 'PERSONNEL' | 'VEHICLE' | 'EQUIPMENT' | 'OTHER'
export type RequestStatus = 'PENDING' | 'ASSIGNED' | 'PARTIAL' | 'REJECTED' | 'CANCELLED'
export const RESOURCE_KIND_LABEL: Record<ResourceKind, string> = { PERSONNEL: 'Personal', VEHICLE: 'Vehículo', EQUIPMENT: 'Equipo', OTHER: 'Otro' }
export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
    PENDING: 'Pendiente', ASSIGNED: 'Asignada', PARTIAL: 'Parcial', REJECTED: 'Rechazada', CANCELLED: 'Cancelada',
}

export interface ResourceRequest {
    id: number
    kind: ResourceKind
    quantity: number
    description: string
    target: string | null
    neededBy: string | null
    status: RequestStatus
    assignedQty: number
    responseNote: string | null
    requestedName: string | null
    respondedName: string | null
    respondedAt: string | null
    createdAt: string
}

export interface Deployment {
    id: number
    requestId: number | null
    kind: ResourceKind
    userId: string | null
    label: string
    organization: string | null
    mission: string | null
    sector: string | null
    startedAt: string
    plannedUntil: string | null
    releasedAt: string | null
    active: boolean
}

export interface ResourceSummary {
    activeDeployments: number
    personnelDeployed: number
    personnelAvailable: number
    personnelDeployedAnywhere: number
    committedPct: number
    pendingRequests: number
}

export const resourceService = {
    async summary(emergencyId: number): Promise<ResourceSummary> { return (await apiClient.get(`${BASE}/${emergencyId}/resources/summary`)).data },
    async requests(emergencyId: number): Promise<ResourceRequest[]> { return (await apiClient.get(`${BASE}/${emergencyId}/resources/requests`)).data },
    async request(emergencyId: number, input: { kind: ResourceKind; quantity: number; description: string; target?: string; neededBy?: string }): Promise<ResourceRequest> {
        return (await apiClient.post(`${BASE}/${emergencyId}/resources/requests`, input)).data
    },
    async respond(emergencyId: number, id: number, input: { status: RequestStatus; assignedQty?: number; note?: string }): Promise<ResourceRequest> {
        return (await apiClient.post(`${BASE}/${emergencyId}/resources/requests/${id}/respond`, input)).data
    },
    async deployments(emergencyId: number): Promise<Deployment[]> { return (await apiClient.get(`${BASE}/${emergencyId}/resources/deployments`)).data },
    async deploy(emergencyId: number, input: { kind: ResourceKind; userId?: string; label?: string; organization?: string; mission?: string; sector?: string; plannedUntil?: string; requestId?: number }): Promise<Deployment> {
        return (await apiClient.post(`${BASE}/${emergencyId}/resources/deployments`, input)).data
    },
    async extend(emergencyId: number, id: number, plannedUntil: string): Promise<Deployment> {
        return (await apiClient.post(`${BASE}/${emergencyId}/resources/deployments/${id}/extend`, { plannedUntil })).data
    },
    async release(emergencyId: number, id: number): Promise<Deployment> {
        return (await apiClient.post(`${BASE}/${emergencyId}/resources/deployments/${id}/release`)).data
    },
}
